"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { useSavedViews } from "@/contexts/SavedViewsContext"
import { toast } from "sonner"
import { LuCalendarOff, LuCalendarClock, LuCheck } from "react-icons/lu"
import { cn } from "@/lib/utils"
import type { RelativeDateValue } from "@/data/types/views.types"

// Date preset options matching the API's relative_date values
interface DatePreset {
  label: string
  value: RelativeDateValue
}

const datePresets: DatePreset[] = [
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 14 days', value: 'last_14_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 90 days', value: 'last_90_days' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last month', value: 'last_month' },
  { label: 'This year', value: 'this_year' },
  { label: 'Last year', value: 'last_year' },
]

type DateFilterMode = 'none' | 'relative'

export function SaveViewModal() {
  const { isModalOpen, setIsModalOpen, currentFilters, addSavedView, allFiltersState } = useSavedViews()
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [dateFilterMode, setDateFilterMode] = useState<DateFilterMode>('none')
  const [selectedPreset, setSelectedPreset] = useState<RelativeDateValue>('last_30_days')

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("View title is required")
      return
    }

    setIsSaving(true)
    try {
      await addSavedView({
        title: title.trim(),
        description: description.trim(),
        relativeDate: dateFilterMode === 'relative' ? selectedPreset : null,
        modelSelection: allFiltersState?.modelSelection || { all: true, providers: [], models: [] },
        metadataFilters: currentFilters,
        granularity: allFiltersState?.granularity || 'daily',
        groupBy: allFiltersState?.dimension || 'model',
      })
      setIsModalOpen(false)
      resetForm()
      toast.success("View saved successfully")
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save view"
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const resetForm = () => {
    setTitle("")
    setDescription("")
    setDateFilterMode('none')
    setSelectedPreset('last_30_days')
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    resetForm()
  }

  return (
    <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
      <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
        <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
          <ModalTitle className="text-[20px] font-medium text-[#08090A]">
            Save Current View
          </ModalTitle>
          <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
            Save your current filter configuration as a reusable view for quick access.
          </ModalDescription>
        </ModalHeader>

        <div className="px-[24px] py-[24px] space-y-[20px]">
          {/* View Title */}
          <div className="space-y-[8px]">
            <Label htmlFor="view-title" className="text-[13px] text-[#08090A]">
              View Title *
            </Label>
            <Input
              id="view-title"
              placeholder="e.g., Production GPT-4 Costs"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isSaving}
              className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isSaving) {
                  handleSave()
                }
              }}
            />
          </div>

          {/* Description */}
          <div className="space-y-[8px]">
            <Label htmlFor="view-description" className="text-[13px] text-[#08090A]">
              Description
            </Label>
            <Input
              id="view-description"
              placeholder="Brief description of this view"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSaving}
              className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none"
            />
          </div>

          {/* Date Range Filter Section */}
          <div className="space-y-[12px]">
            <Label className="text-[13px] text-[#08090A]">
              Date Range
            </Label>

            {/* Mode Toggle */}
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => setDateFilterMode('none')}
                disabled={isSaving}
                className={cn(
                  "flex-1 flex items-center justify-center gap-[8px] h-[36px] px-[16px] rounded-[6px] border transition-all",
                  "text-[13px] font-medium cursor-pointer",
                  dateFilterMode === 'none'
                    ? "bg-[#F2EEFF] border-[#654EDA] text-[#654EDA]"
                    : "bg-white border-[#EDEEF3] text-[#08090A] hover:border-[#D1D5DB] hover:bg-[#F7F8F8]",
                  isSaving && "opacity-50 cursor-not-allowed"
                )}
              >
                <LuCalendarOff className="size-4" />
                No date filter
              </button>
              <button
                type="button"
                onClick={() => setDateFilterMode('relative')}
                disabled={isSaving}
                className={cn(
                  "flex-1 flex items-center justify-center gap-[8px] h-[36px] px-[16px] rounded-[6px] border transition-all",
                  "text-[13px] font-medium cursor-pointer",
                  dateFilterMode === 'relative'
                    ? "bg-[#F2EEFF] border-[#654EDA] text-[#654EDA]"
                    : "bg-white border-[#EDEEF3] text-[#08090A] hover:border-[#D1D5DB] hover:bg-[#F7F8F8]",
                  isSaving && "opacity-50 cursor-not-allowed"
                )}
              >
                <LuCalendarClock className="size-4" />
                Relative date
              </button>
            </div>

            {/* Description text for selected mode */}
            <p className="text-[12px] text-[#8A8F98] leading-[18px]">
              {dateFilterMode === 'none'
                ? "View will use whatever date range is currently selected when applied."
                : "View will automatically adjust to show data for the selected relative period."}
            </p>

            {/* Preset Options Grid - Only shown when relative mode is selected */}
            {dateFilterMode === 'relative' && (
              <div className="grid grid-cols-2 gap-[6px] pt-[4px]">
                {datePresets.map((preset) => {
                  const isSelected = selectedPreset === preset.value
                  return (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => setSelectedPreset(preset.value)}
                      disabled={isSaving}
                      className={cn(
                        "relative flex items-center justify-between h-[32px] px-[12px] rounded-[6px] border transition-all",
                        "text-[13px] cursor-pointer text-left",
                        isSelected
                          ? "bg-[#F2EEFF] border-[#654EDA] text-[#654EDA] font-medium"
                          : "bg-white border-[#EDEEF3] text-[#08090A] hover:border-[#D1D5DB] hover:bg-[#F7F8F8]",
                        isSaving && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <span>{preset.label}</span>
                      {isSelected && (
                        <LuCheck className="size-4 text-[#654EDA]" />
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
          <Button
            variant="transparent-outline"
            size="sm"
            onClick={handleCancel}
            disabled={isSaving}
            className="h-[32px] font-medium border-[#EDEEF3]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
          >
            {isSaving ? "Saving..." : "Save View"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
